/**
 * Departments dashboard
 *
 * - Header with quick actions (new department, new ministry)
 * - Stat tiles: departments, people in departments, designations, ministries, people in ministries,
 *   departments without a head
 * - Charts: members per department, designations per department, ministry sizes, status split
 * - Needs attention: departments with no head, no members or no designations
 * - Recent departments and ministries
 *
 * Data: GET api/departments/view and GET api/ministries/view (the shared list-view keys).
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMantineTheme, useComputedColorScheme } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBriefcase,
  IconBuildingChurch,
  IconChevronRight,
  IconCircleCheck,
  IconCrown,
  IconHierarchy,
  IconPlus,
  IconRefresh,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Button, Card, Grid, Group, SimpleGrid, Skeleton, Stack, Text } from '@/components/ui';
import { Avatar } from '@/components/ui/data-display';
import { FadeIn } from '@/components/animations';
import BarChartWidget from '@/components/advanced/Widgets/charts/BarChartWidget';
import DonutChartWidget from '@/components/advanced/Widgets/charts/DonutChartWidget';
import { useDepartmentService, useMinistryService, type DepartmentModelView, type MinistryModelView } from '@actschurch/ccms-sdk';
import { useDepartmentsPermissions } from '@/hooks/departments/useDepartmentsPermissions';
import {
  ActiveBadge,
  DEPARTMENTS_LIST_PATH,
  DEPARTMENT_MEMBERS_PATH,
  DESIGNATIONS_PATH,
  DepartmentFormDrawer,
  DepartmentsGate,
  ErrorNotice,
  MINISTRIES_PATH,
  MINISTRY_MEMBERS_PATH,
  PageHeader,
  SectionCard,
  StatTile,
  StatTilesSkeleton,
  Tag,
  byName,
  departmentPath,
  departmentsKeys,
  formatNumber,
  initials,
  ministriesKeys,
  ministryPath,
  plural,
} from '../components';
import MinistryFormDrawer from '../ministries/components/MinistryFormDrawer';

const PALETTE = ['blue', 'teal', 'orange', 'grape', 'cyan', 'lime', 'yellow', 'red', 'indigo', 'pink'] as const;

interface Issue {
  kind: 'no-head' | 'no-members' | 'no-designations';
  department: DepartmentModelView;
}

function issuesFor(d: DepartmentModelView): Issue[] {
  if (!d.isActive) return [];
  const out: Issue[] = [];
  if (!d.departmentHead) out.push({ kind: 'no-head', department: d });
  if ((d.memberCount ?? 0) === 0) out.push({ kind: 'no-members', department: d });
  if ((d.designations ?? 0) === 0) out.push({ kind: 'no-designations', department: d });
  return out;
}

const ISSUE_TEXT: Record<Issue['kind'], string> = {
  'no-head': 'No department head',
  'no-members': 'No members',
  'no-designations': 'No designations',
};

function DepartmentRow({ d, onOpen }: { d: DepartmentModelView; onOpen: () => void }) {
  return (
    <Card withBorder radius="md" padding="sm" onClick={onOpen} style={{ cursor: 'pointer', opacity: d.isActive ? 1 : 0.75 }}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Avatar color="blue" radius="xl" size="sm">
            {initials(d.name)}
          </Avatar>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} lineClamp={1}>
              {d.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {plural(d.memberCount ?? 0, 'member')}, {plural(d.designations ?? 0, 'designation')}
              {d.departmentHead ? `, led by ${d.departmentHead}` : ''}
            </Text>
          </Stack>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {!d.departmentHead && d.isActive && <Tag color="orange">No head</Tag>}
          <ActiveBadge isActive={d.isActive} size="xs" />
          <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />
        </Group>
      </Group>
    </Card>
  );
}

function MinistryRow({ m, onOpen }: { m: MinistryModelView; onOpen: () => void }) {
  return (
    <Card withBorder radius="md" padding="sm" onClick={onOpen} style={{ cursor: 'pointer', opacity: m.isActive ? 1 : 0.75 }}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Avatar color="grape" radius="xl" size="sm">
            {initials(m.name)}
          </Avatar>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} lineClamp={1}>
              {m.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {plural(m.ministryMembers ?? 0, 'member')}, {plural(m.ministryTeams ?? 0, 'team')}, {plural(m.ministryActivities ?? 0, 'activity', 'activities')}
            </Text>
          </Stack>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <ActiveBadge isActive={m.isActive} size="xs" />
          <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />
        </Group>
      </Group>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <Card withBorder padding="lg" radius="md">
      <Skeleton height={300} radius="md" />
    </Card>
  );
}

export default function DepartmentsDashboard() {
  return (
    <DepartmentsGate allow={(p) => p.canOpenModule}>
      <DashboardContent />
    </DepartmentsGate>
  );
}

function DashboardContent() {
  const navigate = useNavigate();
  const perms = useDepartmentsPermissions();
  const departmentService = useDepartmentService();
  const ministryService = useMinistryService();
  const theme = useMantineTheme();
  const isDark = useComputedColorScheme('light') === 'dark';
  const tone = (name: (typeof PALETTE)[number]) => theme.colors[name]?.[isDark ? 4 : 6] ?? '#3B82F6';

  const [newDepartment, setNewDepartment] = useState(false);
  const [newMinistry, setNewMinistry] = useState(false);

  const departments = useQuery({
    queryKey: departmentsKeys.views,
    queryFn: () => departmentService.getAllModelViews(),
    enabled: perms.canViewDepartments,
    staleTime: 60_000,
  });
  const ministries = useQuery({
    queryKey: ministriesKeys.views,
    queryFn: () => ministryService.getAllMinistryViews(),
    enabled: perms.canViewMinistries,
    staleTime: 60_000,
  });

  const depts = useMemo(() => (departments.data ?? []).slice().sort(byName), [departments.data]);
  const mins = useMemo(() => (ministries.data ?? []).slice().sort(byName), [ministries.data]);

  const stats = useMemo(() => {
    const activeDepts = depts.filter((d) => d.isActive);
    const activeMins = mins.filter((m) => m.isActive);
    return {
      departments: depts.length,
      activeDepartments: activeDepts.length,
      departmentMembers: depts.reduce((s, d) => s + (d.memberCount ?? 0), 0),
      designations: depts.reduce((s, d) => s + (d.designations ?? 0), 0),
      withoutHead: activeDepts.filter((d) => !d.departmentHead).length,
      ministries: mins.length,
      activeMinistries: activeMins.length,
      ministryMembers: mins.reduce((s, m) => s + (m.ministryMembers ?? 0), 0),
      ministryTeams: mins.reduce((s, m) => s + (m.ministryTeams ?? 0), 0),
    };
  }, [depts, mins]);

  const issues = useMemo(() => depts.flatMap(issuesFor), [depts]);

  // Charts
  const membersPerDepartment = useMemo(
    () =>
      depts
        .filter((d) => d.isActive)
        .map((d) => ({ department: d.name, members: d.memberCount ?? 0, designations: d.designations ?? 0 }))
        .sort((a, b) => b.members - a.members || b.designations - a.designations)
        .slice(0, 10),
    [depts]
  );
  const ministrySizes = useMemo(() => {
    const rows = mins
      .filter((m) => m.isActive && (m.ministryMembers ?? 0) > 0)
      .map((m) => ({ name: m.name, value: m.ministryMembers ?? 0 }))
      .sort((a, b) => b.value - a.value);
    const top = rows.slice(0, 7).map((r, i) => ({ ...r, color: tone(PALETTE[i % PALETTE.length]) }));
    const rest = rows.slice(7).reduce((s, r) => s + r.value, 0);
    if (rest > 0) top.push({ name: 'Other ministries', value: rest, color: theme.colors.gray[isDark ? 5 : 5] });
    return top;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mins, isDark]);
  const ministryBreakdown = useMemo(
    () =>
      mins
        .filter((m) => m.isActive)
        .map((m) => ({ ministry: m.name, teams: m.ministryTeams ?? 0, activities: m.ministryActivities ?? 0, equipment: m.ministryEquipments ?? 0 }))
        .sort((a, b) => b.teams + b.activities + b.equipment - (a.teams + a.activities + a.equipment))
        .slice(0, 10),
    [mins]
  );
  const statusSplit = useMemo(
    () => [
      { name: 'Active departments', value: stats.activeDepartments, color: tone('blue') },
      { name: 'Inactive departments', value: stats.departments - stats.activeDepartments, color: theme.colors.gray[isDark ? 6 : 4] },
      { name: 'Active ministries', value: stats.activeMinistries, color: tone('grape') },
      { name: 'Inactive ministries', value: stats.ministries - stats.activeMinistries, color: theme.colors.gray[isDark ? 7 : 3] },
    ].filter((s) => s.value > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stats, isDark]
  );

  const loading = departments.isLoading || ministries.isLoading;
  const refresh = () => {
    departments.refetch();
    ministries.refetch();
  };

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <PageHeader
            title="Departments"
            description="Staff departments and their designations, and the ministries people serve in."
            actions={
              <>
                <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={refresh} loading={(departments.isFetching || ministries.isFetching) && !loading}>
                  Refresh
                </Button>
                {perms.canManageMinistries && (
                  <Button variant="light" color="grape" leftSection={<IconPlus size={16} />} onClick={() => setNewMinistry(true)}>
                    New ministry
                  </Button>
                )}
                {perms.canManageDepartments && (
                  <Button leftSection={<IconPlus size={16} />} onClick={() => setNewDepartment(true)}>
                    New department
                  </Button>
                )}
              </>
            }
          />

          <ErrorNotice error={departments.error} title="Could not load the departments" onRetry={() => departments.refetch()} />
          <ErrorNotice error={ministries.error} title="Could not load the ministries" onRetry={() => ministries.refetch()} />

          {loading ? (
            <StatTilesSkeleton count={6} />
          ) : (
            <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 6 }}>
              <StatTile icon={<IconHierarchy size={22} />} color="blue" label="Departments" value={formatNumber(stats.activeDepartments)} hint={stats.departments > stats.activeDepartments ? `${formatNumber(stats.departments - stats.activeDepartments)} inactive` : 'All active'} onClick={() => navigate(DEPARTMENTS_LIST_PATH)} />
              <StatTile icon={<IconUsers size={22} />} color="teal" label="People in departments" value={formatNumber(stats.departmentMembers)} hint="Active memberships" onClick={() => navigate(DEPARTMENT_MEMBERS_PATH)} />
              <StatTile icon={<IconBriefcase size={22} />} color="orange" label="Designations" value={formatNumber(stats.designations)} hint="Across all departments" onClick={() => navigate(DESIGNATIONS_PATH)} />
              <StatTile icon={<IconBuildingChurch size={22} />} color="grape" label="Ministries" value={formatNumber(stats.activeMinistries)} hint={stats.ministries > stats.activeMinistries ? `${formatNumber(stats.ministries - stats.activeMinistries)} inactive` : 'All active'} onClick={() => navigate(MINISTRIES_PATH)} />
              <StatTile icon={<IconUsersGroup size={22} />} color="cyan" label="People in ministries" value={formatNumber(stats.ministryMembers)} hint={plural(stats.ministryTeams, 'team')} onClick={() => navigate(MINISTRY_MEMBERS_PATH)} />
              <StatTile icon={stats.withoutHead > 0 ? <IconAlertTriangle size={22} /> : <IconCircleCheck size={22} />} color={stats.withoutHead > 0 ? 'red' : 'green'} emphasis={stats.withoutHead > 0} label="Without a head" value={formatNumber(stats.withoutHead)} hint={stats.withoutHead > 0 ? 'Active departments need a head' : 'Every department has a head'} onClick={() => navigate(DEPARTMENTS_LIST_PATH)} />
            </SimpleGrid>
          )}

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, lg: 7 }}>
              {loading ? (
                <ChartSkeleton />
              ) : (
                <BarChartWidget
                  title="Members and designations per department"
                  subtitle={membersPerDepartment.length === 0 ? 'No active departments yet' : 'Largest departments first'}
                  data={membersPerDepartment}
                  bars={[
                    { dataKey: 'members', name: 'Members', color: tone('blue') },
                    { dataKey: 'designations', name: 'Designations', color: tone('orange') },
                  ]}
                  xAxisKey="department"
                  height={320}
                  showLegend
                  headerActions={
                    <Button variant="subtle" size="compact-xs" component={Link} to={DEPARTMENTS_LIST_PATH}>
                      See all
                    </Button>
                  }
                />
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 5 }}>
              {loading ? (
                <ChartSkeleton />
              ) : (
                <DonutChartWidget
                  title="People per ministry"
                  subtitle={ministrySizes.length === 0 ? 'No ministry members yet' : `${formatNumber(stats.ministryMembers)} people serving`}
                  data={ministrySizes}
                  showPercentages
                  showLegend
                  height={320}
                  centerLabel={formatNumber(stats.ministryMembers)}
                  headerActions={
                    <Button variant="subtle" size="compact-xs" component={Link} to={MINISTRY_MEMBERS_PATH}>
                      See all
                    </Button>
                  }
                />
              )}
            </Grid.Col>
          </Grid>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, lg: 7 }}>
              {loading ? (
                <ChartSkeleton />
              ) : (
                <BarChartWidget
                  title="What each ministry runs"
                  subtitle={ministryBreakdown.length === 0 ? 'No active ministries yet' : 'Teams, activities and equipment per ministry'}
                  data={ministryBreakdown}
                  bars={[
                    { dataKey: 'teams', name: 'Teams', color: tone('grape'), stackId: 'm' },
                    { dataKey: 'activities', name: 'Activities', color: tone('cyan'), stackId: 'm' },
                    { dataKey: 'equipment', name: 'Equipment', color: tone('teal'), stackId: 'm' },
                  ]}
                  stacked
                  xAxisKey="ministry"
                  height={300}
                  showLegend
                  headerActions={
                    <Button variant="subtle" size="compact-xs" component={Link} to={MINISTRIES_PATH}>
                      See all
                    </Button>
                  }
                />
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 5 }}>
              {loading ? (
                <ChartSkeleton />
              ) : (
                <DonutChartWidget
                  title="Active and inactive"
                  subtitle="Departments and ministries by status"
                  data={statusSplit}
                  showPercentages
                  showLegend
                  height={300}
                  centerLabel={formatNumber(stats.departments + stats.ministries)}
                />
              )}
            </Grid.Col>
          </Grid>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <SectionCard icon={issues.length > 0 ? <IconAlertTriangle size={18} /> : <IconCircleCheck size={18} />} color={issues.length > 0 ? 'orange' : 'green'} title="Needs attention" description={issues.length > 0 ? plural(issues.length, 'gap') : 'Nothing outstanding'}>
                {loading ? (
                  <Stack gap="xs">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={44} radius="md" />
                    ))}
                  </Stack>
                ) : issues.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Every active department has a head, members and designations.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {issues.slice(0, 8).map((issue) => (
                      <Group key={`${issue.kind}-${issue.department.departmentID}`} justify="space-between" wrap="nowrap" style={{ cursor: 'pointer' }} onClick={() => navigate(departmentPath(issue.department.departmentID, issue.kind === 'no-designations' ? 'designations' : 'members'))}>
                        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                          {issue.kind === 'no-head' ? <IconCrown size={16} color="var(--mantine-color-orange-6)" /> : issue.kind === 'no-members' ? <IconUsers size={16} color="var(--mantine-color-red-6)" /> : <IconBriefcase size={16} color="var(--mantine-color-yellow-7)" />}
                          <Text size="sm" lineClamp={1}>
                            {issue.department.name}
                          </Text>
                        </Group>
                        <Tag color={issue.kind === 'no-members' ? 'red' : 'orange'}>{ISSUE_TEXT[issue.kind]}</Tag>
                      </Group>
                    ))}
                    {issues.length > 8 && (
                      <Text size="xs" c="dimmed">
                        and {plural(issues.length - 8, 'more gap')}
                      </Text>
                    )}
                  </Stack>
                )}
              </SectionCard>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <SectionCard
                icon={<IconHierarchy size={18} />}
                color="blue"
                title="Departments"
                description={plural(stats.departments, 'department')}
                actions={
                  <Button variant="subtle" size="compact-sm" component={Link} to={DEPARTMENTS_LIST_PATH}>
                    See all
                  </Button>
                }
              >
                {loading ? (
                  <Stack gap="xs">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={54} radius="md" />
                    ))}
                  </Stack>
                ) : depts.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No departments yet.{perms.canManageDepartments ? ' Use New department to add the first one.' : ''}
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {depts.slice(0, 6).map((d) => (
                      <DepartmentRow key={d.departmentID} d={d} onOpen={() => navigate(departmentPath(d.departmentID))} />
                    ))}
                  </Stack>
                )}
              </SectionCard>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <SectionCard
                icon={<IconBuildingChurch size={18} />}
                color="grape"
                title="Ministries"
                description={plural(stats.ministries, 'ministry', 'ministries')}
                actions={
                  <Button variant="subtle" size="compact-sm" component={Link} to={MINISTRIES_PATH}>
                    See all
                  </Button>
                }
              >
                {loading ? (
                  <Stack gap="xs">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={54} radius="md" />
                    ))}
                  </Stack>
                ) : mins.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No ministries yet.{perms.canManageMinistries ? ' Use New ministry to add the first one.' : ''}
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {mins.slice(0, 6).map((m) => (
                      <MinistryRow key={m.ministryID} m={m} onOpen={() => navigate(ministryPath(m.ministryID))} />
                    ))}
                  </Stack>
                )}
              </SectionCard>
            </Grid.Col>
          </Grid>
        </Stack>
      </FadeIn>

      <DepartmentFormDrawer opened={newDepartment} onClose={() => setNewDepartment(false)} onSaved={(saved) => navigate(departmentPath(saved.departmentID))} />
      <MinistryFormDrawer opened={newMinistry} onClose={() => setNewMinistry(false)} onSaved={(saved) => navigate(ministryPath(saved.ministryID))} />
    </LayoutContainer>
  );
}
